import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { useMutation, useQuery } from '@tanstack/react-query';
import deepEqual from 'deep-equal';

import {
  fetchPageFilterPersistence,
  mutatePageFilterPersistence,
  type PageFilterPersistenceQueryKey,
} from '../api/portalGateway/pageFilterPersistence';
import { type FilterPersistenceMode } from '../components/filter/FilterPersistenceIcon';
import i18n from '../i18n/config';
import { useStore } from '../store';
import { type FilterSelections } from '../store/states/filterSelection';
import { actionWithAsyncNotification } from '../utils/actionWithAsyncNotification';
import { CUSTOM_FILTER_INSTANCE_ID_SUFFIX } from '../utils/constants';
import { prependRoleId } from '../utils/prependRoleId';

type PageFilterConfig = PageDto['pageFilter'];

type PersistenceConfig = {
  pageFilterPersistence: NonNullable<PageDto['pageFilterPersistence']>;
  pageFilterPersistenceScope?: NonNullable<
    PageDto['pageFilterPersistenceScope']
  >;
};

const AUTO_SAVE_DEBOUNCE_MS = 1000;

function getDefaultAutoSaveEnabled(
  config: PersistenceConfig['pageFilterPersistence'] | undefined
): boolean | null {
  switch (config) {
    case 'auto-default-enabled':
    case 'auto-forced-enabled':
      return true;
    case 'auto-default-disabled':
      return false;
    default:
      return null;
  }
}

function getEffectiveAutoSaveEnabled(
  config: PersistenceConfig['pageFilterPersistence'] | undefined,
  persistedAutoSaveEnabled: boolean | null
): boolean {
  const defaultAutoSaveEnabled = getDefaultAutoSaveEnabled(config);
  return persistedAutoSaveEnabled ?? defaultAutoSaveEnabled ?? false;
}

/**
 * Used to determine whether the user preference for automatic saving should be persisted.
 *
 * If the user preference matches the configured default, null is returned instead of the actual value. This makes the persistence flexible in regards to later configuration changes.
 */
function getAutoSavePreferenceForPersistence(
  config: PersistenceConfig['pageFilterPersistence'] | undefined,
  autoSaveEnabled: boolean
): boolean | null {
  if (config !== 'auto-default-enabled' && config !== 'auto-default-disabled') {
    return null;
  }

  const defaultAutoSaveEnabled = getDefaultAutoSaveEnabled(config);
  return autoSaveEnabled === defaultAutoSaveEnabled ? null : autoSaveEnabled;
}

/**
 * Maps the configured persistence mode to the currently active persistence mode.
 */
function deriveMode(
  config: PersistenceConfig['pageFilterPersistence'] | undefined,
  autoSaveEnabled: boolean
): FilterPersistenceMode | undefined {
  switch (config) {
    case 'manual':
      return 'manualSave';
    case 'auto-forced-enabled':
      return 'autoSaveForceEnabled';
    case 'auto-default-enabled':
    case 'auto-default-disabled':
      return autoSaveEnabled ? 'autoSaveEnabled' : 'autoSaveDisabled';
    default:
      return undefined;
  }
}

export type UsePageFilterPersistenceResult = {
  persistenceMode: FilterPersistenceMode | undefined;
  persistedSelections: FilterSelections | undefined;
  persistedCustomSelections: FilterSelections | undefined;
  isLoading: boolean;
  onPersistenceClick: () => void;
};

type UsePageFilterPersistenceParams = {
  persistenceReference: string | null;
  filterStoreInstanceId: string | null;
  persistenceConfig?: PersistenceConfig;
  pageFilterConfig?: PageFilterConfig;
};

function getVisiblePageFilterKeys(
  pageFilterConfig?: PageFilterConfig
): Set<string> | null {
  const filters = pageFilterConfig?.filters;
  if (filters == null) {
    return null;
  }

  return new Set(
    filters
      .filter((filter) => filter.hidden !== true)
      .map((filter) => filter.key)
  );
}

function getAllConfiguredFilterKeys(
  pageFilterConfig?: PageFilterConfig
): Set<string> | null {
  const filters = pageFilterConfig?.filters;
  if (filters == null) {
    return null;
  }

  return new Set(filters.map((filter) => filter.key));
}

function filterSelectionsByKeys(
  selections: FilterSelections | undefined,
  keys: Set<string> | null,
  mode: 'include' | 'exclude'
): FilterSelections | undefined {
  if (selections == null || keys == null) {
    return selections;
  }

  return Object.fromEntries(
    Object.entries(selections).filter(([key]) =>
      mode === 'include' ? keys.has(key) : !keys.has(key)
    )
  );
}

export function usePageFilterPersistence({
  persistenceReference,
  filterStoreInstanceId,
  persistenceConfig,
  pageFilterConfig,
}: UsePageFilterPersistenceParams): UsePageFilterPersistenceResult {
  const config = persistenceConfig?.pageFilterPersistence;
  const isEnabled = config != null;
  const isGlobal = persistenceConfig?.pageFilterPersistenceScope === 'global';
  const visiblePageFilterKeys = useMemo(
    () => getVisiblePageFilterKeys(pageFilterConfig),
    [pageFilterConfig]
  );

  const allConfiguredFilterKeys = useMemo(
    () => getAllConfiguredFilterKeys(pageFilterConfig),
    [pageFilterConfig]
  );

  const hasCustomPageFilters = useMemo(
    () =>
      pageFilterConfig?.customizablePageFilters === true &&
      pageFilterConfig?.customPageFilterColumns != null &&
      pageFilterConfig.customPageFilterColumns.length > 0,
    [pageFilterConfig]
  );

  const customFilterStoreInstanceId = useMemo(
    () =>
      hasCustomPageFilters && filterStoreInstanceId
        ? `${filterStoreInstanceId}${CUSTOM_FILTER_INSTANCE_ID_SUFFIX}`
        : null,
    [hasCustomPageFilters, filterStoreInstanceId]
  );

  const [autoSaveEnabled, setAutoSaveEnabled] = useState(() =>
    getEffectiveAutoSaveEnabled(config, null)
  );

  const { data, isLoading } = useQuery({
    queryKey: prependRoleId<PageFilterPersistenceQueryKey>([
      'pageFilterPersistence',
      { reference: persistenceReference ?? '', global: isGlobal },
    ]),
    queryFn: fetchPageFilterPersistence,
    enabled: isEnabled && persistenceReference != null,
    retry: false,
  });

  const persistedAutoSaveEnabled = data?.data.autoSaveEnabled ?? null;
  const persistedSelections = useMemo(
    () =>
      filterSelectionsByKeys(
        data?.data.selections,
        visiblePageFilterKeys,
        'include'
      ),
    [data?.data.selections, visiblePageFilterKeys]
  );

  const persistedCustomSelections = useMemo(
    () =>
      hasCustomPageFilters
        ? filterSelectionsByKeys(
            data?.data.selections,
            allConfiguredFilterKeys,
            'exclude'
          )
        : undefined,
    [data?.data.selections, allConfiguredFilterKeys, hasCustomPageFilters]
  );

  // Reset the effective toggle when config or persisted preference changes.
  useEffect(() => {
    setAutoSaveEnabled(
      getEffectiveAutoSaveEnabled(config, persistedAutoSaveEnabled)
    );
  }, [config, persistedAutoSaveEnabled]);

  const { mutate, mutateAsync } = useMutation({
    mutationFn: mutatePageFilterPersistence,
  });

  const filterInitialized = useStore(
    (state) =>
      (filterStoreInstanceId
        ? state.filterInitialized[filterStoreInstanceId]
        : false) ?? false
  );

  const rawFilterSelections = useStore((state) =>
    filterStoreInstanceId
      ? state.filterSelections[filterStoreInstanceId]
      : undefined
  );

  const filterSelections = useMemo(
    () =>
      filterSelectionsByKeys(
        rawFilterSelections,
        visiblePageFilterKeys,
        'include'
      ),
    [rawFilterSelections, visiblePageFilterKeys]
  );

  const rawCustomFilterSelections = useStore((state) =>
    customFilterStoreInstanceId
      ? state.filterSelections[customFilterStoreInstanceId]
      : undefined
  );

  // Before custom filter data has loaded, fall back to persisted custom
  // selections so that early auto-saves preserve them.
  const effectiveCustomSelections = useMemo(
    () =>
      hasCustomPageFilters
        ? (rawCustomFilterSelections ?? persistedCustomSelections)
        : undefined,
    [hasCustomPageFilters, rawCustomFilterSelections, persistedCustomSelections]
  );

  const selectionsForPersistence = useMemo(
    () => ({
      ...filterSelections,
      ...effectiveCustomSelections,
    }),
    [filterSelections, effectiveCustomSelections]
  );

  const allPersistedSelections = useMemo(
    () => ({
      ...persistedSelections,
      ...persistedCustomSelections,
    }),
    [persistedSelections, persistedCustomSelections]
  );

  const getCurrentSelectionsForPersistence =
    useCallback((): FilterSelections => {
      if (filterStoreInstanceId == null) {
        return {};
      }
      const state = useStore.getState();
      const configured =
        filterSelectionsByKeys(
          state.filterSelections[filterStoreInstanceId],
          visiblePageFilterKeys,
          'include'
        ) ?? {};
      const custom = customFilterStoreInstanceId
        ? (state.filterSelections[customFilterStoreInstanceId] ??
          persistedCustomSelections ??
          {})
        : {};
      return { ...configured, ...custom };
    }, [
      filterStoreInstanceId,
      customFilterStoreInstanceId,
      visiblePageFilterKeys,
      persistedCustomSelections,
    ]);

  // Auto-save: debounce filter changes and persist when auto-save is on
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const prevSelectionsRef = useRef<FilterSelections | undefined>(undefined);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!filterInitialized) {
      hasInitializedRef.current = false;
      return;
    }

    const shouldAutoSave =
      isEnabled &&
      persistenceReference != null &&
      filterStoreInstanceId != null &&
      autoSaveEnabled &&
      config !== 'manual';

    const scheduleAutoSave = (
      selectionsToPersist: FilterSelections | undefined
    ) => {
      if (persistenceReference == null) {
        return;
      }

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        mutate({
          reference: persistenceReference,
          data: {
            autoSaveEnabled: getAutoSavePreferenceForPersistence(
              config,
              autoSaveEnabled
            ),
            selections: selectionsToPersist ?? {},
          },
          global: isGlobal,
        });
      }, AUTO_SAVE_DEBOUNCE_MS);
    };

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      prevSelectionsRef.current = selectionsForPersistence;

      if (!shouldAutoSave) {
        return;
      }

      if (deepEqual(selectionsForPersistence, allPersistedSelections)) {
        return;
      }

      scheduleAutoSave(selectionsForPersistence);
      return () => clearTimeout(debounceRef.current);
    }

    if (!shouldAutoSave) {
      prevSelectionsRef.current = selectionsForPersistence;
      return;
    }

    if (selectionsForPersistence === prevSelectionsRef.current) {
      return;
    }

    prevSelectionsRef.current = selectionsForPersistence;

    scheduleAutoSave(selectionsForPersistence);

    return () => clearTimeout(debounceRef.current);
  }, [
    selectionsForPersistence,
    filterInitialized,
    autoSaveEnabled,
    config,
    isEnabled,
    isGlobal,
    persistenceReference,
    filterStoreInstanceId,
    allPersistedSelections,
    mutate,
  ]);

  const persistenceMode = useMemo(
    () => (isEnabled ? deriveMode(config, autoSaveEnabled) : undefined),
    [config, autoSaveEnabled, isEnabled]
  );

  const onPersistenceClick = useCallback(() => {
    if (
      config === 'manual' &&
      persistenceReference != null &&
      filterStoreInstanceId != null
    ) {
      const selections = getCurrentSelectionsForPersistence();
      void actionWithAsyncNotification(
        () =>
          mutateAsync({
            reference: persistenceReference,
            data: {
              autoSaveEnabled: null,
              selections,
            },
            global: isGlobal,
          }),
        {
          startMessage: i18n.t('app.notifications.filterPersistence.loading'),
          successMessage: i18n.t('app.notifications.filterPersistence.success'),
          errorMessage: i18n.t('app.notifications.filterPersistence.error'),
        }
      );
      return;
    }

    if (
      (config === 'auto-default-enabled' ||
        config === 'auto-default-disabled') &&
      persistenceReference != null &&
      filterStoreInstanceId != null
    ) {
      const nextAutoSaveEnabled = !autoSaveEnabled;
      setAutoSaveEnabled(nextAutoSaveEnabled);

      const selections = getCurrentSelectionsForPersistence();
      mutate({
        reference: persistenceReference,
        data: {
          autoSaveEnabled: getAutoSavePreferenceForPersistence(
            config,
            nextAutoSaveEnabled
          ),
          selections,
        },
        global: isGlobal,
      });
    }
  }, [
    autoSaveEnabled,
    config,
    isGlobal,
    persistenceReference,
    filterStoreInstanceId,
    mutate,
    mutateAsync,
    getCurrentSelectionsForPersistence,
  ]);

  return {
    persistenceMode,
    persistedSelections,
    persistedCustomSelections,
    isLoading,
    onPersistenceClick,
  };
}
